import { useCallback } from "react";
import { useReverification, useUser } from "@clerk/react";
import { isReverificationCancelledError } from "@clerk/react/errors";

export type CreatePasskeyResult =
  | { status: "enrolled" }
  | { status: "cancelled" }
  | { status: "error"; message: string };

const CANCEL_CODES = new Set([
  "passkey_registration_cancelled",
  "passkey_operation_aborted",
]);

const FALLBACK_MESSAGE = "We couldn't set up a passkey. Try again later.";

function isCancellation(err: unknown): boolean {
  if (isReverificationCancelledError(err)) return true;
  if (err && typeof err === "object") {
    const { name, code } = err as { name?: unknown; code?: unknown };
    // The browser refused/dismissed the WebAuthn ceremony.
    if (name === "NotAllowedError" || name === "AbortError") return true;
    if (typeof code === "string" && CANCEL_CODES.has(code)) return true;
  }
  return false;
}

function messageFrom(err: unknown): string {
  if (err && typeof err === "object") {
    const errors = (err as { errors?: unknown }).errors;
    if (Array.isArray(errors) && errors[0] && typeof errors[0] === "object") {
      const first = errors[0] as { longMessage?: unknown; message?: unknown };
      if (typeof first.longMessage === "string") return first.longMessage;
      if (typeof first.message === "string") return first.message;
    }
  }
  return FALLBACK_MESSAGE;
}

/**
 * The ONE passkey-enrollment ceremony: `user.createPasskey()` under Clerk's
 * own reverification modal (design I9 — no `onNeedsReverification` override).
 * UI-free; callers own toasts, copy and dismissal. Pro-local copy of the
 * consumer hook: the pro app must not import from the consumer app.
 */
export function useCreatePasskey(): () => Promise<CreatePasskeyResult> {
  const { user } = useUser();
  const create = useReverification(async () => {
    if (!user) throw new Error("not signed in");
    await user.createPasskey();
  });
  return useCallback(async (): Promise<CreatePasskeyResult> => {
    try {
      await create();
      return { status: "enrolled" };
    } catch (err) {
      if (isCancellation(err)) return { status: "cancelled" };
      return { status: "error", message: messageFrom(err) };
    }
  }, [create]);
}
